<template>
    <div class="animated fadeIn">
        <LoadingWidget
            v-if="initStatus != 1"
            :status="initStatus"
            @init="init"
        />
        <b-card v-else>
            <h5 class="mb-0">
                <i class="fa fa-fw fa-dollar mr-2" />Rumus Harga Marketplace
            </h5>
            <hr />
            <b-row>
                <b-col md="3">
                    <b-form-group label="Jenis Barang">
                        <b-form-input
                            v-model="filter.kind"
                            maxlength="50"
                            placeholder="Cari Jenis Barang"
                            @keydown.enter="getItems"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Aksi">
                        <b-button variant="primary" @click="getItems()">
                            <i class="fa fa-search mr-2" />Cari
                        </b-button>
                    </b-form-group>
                </b-col>
                <b-col md="6" class="text-right">
                    <b-form-group label="Tambah">
                        <b-button variant="success" @click="prepareCreate()">
                            <i class="fa fa-plus mr-2" />Rumus Baru
                        </b-button>
                    </b-form-group>
                </b-col>
            </b-row>
            <b-table
                bordered
                hover
                show-empty
                no-local-sorting
                :busy="isBusy"
                :items="items"
                :fields="fields"
                @sort-changed="onSortChanged"
                responsive
                class="mb-3"
            >
                <template v-slot:empty>
                    Tidak ada data rumus harga marketplace.
                </template>
                <template v-slot:cell(rounding)="row">
                    Rp. {{ $helper.formatThousand(row.item.rounding) }}
                </template>
                <template v-slot:cell(action)="row">
                    <b-button
                        size="sm"
                        variant="info"
                        v-b-tooltip.hover.top
                        title="Detail"
                        @click="showDetail(row.item)"
                        class="mr-1"
                    >
                         <i class="fa fa-fw fa-info" />
                    </b-button>
                    <b-button
                        size="sm"
                        variant="warning"
                        v-b-tooltip.hover.top
                        title="Edit"
                        @click="prepareEdit(row.item)"
                        class="mr-1"
                    >
                        <i class="fa fa-fw fa-pencil" />
                    </b-button>
                </template>
            </b-table>
            <PageSizer
                :totalRows="totalRows"
                :page="currentPage"
                :options="options"
                @limit-change="onLimitChange"
                @page-change="onPageChange"
            />
        </b-card>

        <b-modal
            id="m-detail"
            title="Detail Range Harga"
            size="lg"
            no-close-on-backdrop
            no-close-on-esc
            hide-footer
        >
            Jenis Barang
            <strong>{{ detail.item_kind_name }}</strong>

            <b-table
                bordered
                hover
                show-empty
                responsive
                :items="detail.ranges"
                :fields="fieldsRangeDetail"
                empty-text="Tidak ada data range."
            >
                <template v-slot:empty>
                    Tidak ada data range.
                </template>
                <template v-slot:cell(from)="row">
                    <b-input-group prepend="Rp.">
                        <b-form-input v-model="row.item.from" disabled />
                    </b-input-group>
                </template>
                <template v-slot:cell(to)="row">
                    <b-input-group prepend="Rp.">
                        <b-form-input v-model="row.item.to" disabled />
                    </b-input-group>
                </template>
                <template v-slot:cell(percentage)="row">
                    <b-input-group append="%">
                        <b-form-input v-model="row.item.percentage" disabled />
                    </b-input-group>
                </template>
            </b-table>

            <b-form-group label="Pembulatan" class="mt-2">
                <b-input-group prepend="Rp.">
                    <b-input v-model="detail.rounding" disabled />
                </b-input-group>
            </b-form-group>
        </b-modal>

        <b-modal
            id="m-create"
            title="Tambah Rumus Harga Baru"
            size="lg"
            no-close-on-backdrop
            no-close-on-esc
            hide-footer
            @hide="clearInput"
        >
            <b-form-group label="Jenis Barang">
                <b-form-select
                    v-model="input.kindId"
                    :options="optKind"
                    value-field="id"
                    text-field="name"
                />
            </b-form-group>

            <div class="text-right my-4">
                <b-button variant="dark" @click="addRow(-1)">
                    <i class="fa fa-plus mr-2" />Tambah Baris Paling Atas
                </b-button>
            </div>

            <b-table
                bordered
                hover
                show-empty
                responsive
                :items="input.ranges"
                :fields="fieldsRangeInput"
                empty-text="Tidak ada data range."
            >
                <template v-slot:empty>
                    Tidak ada data range.
                </template>
                <template v-slot:cell(from)="row">
                    <b-input-group prepend="Rp.">
                        <b-form-input
                            v-model="row.item.from"
                            @input="formatCurrencyInput(row.item, 'from')"
                        />
                    </b-input-group>
                </template>
                <template v-slot:cell(to)="row">
                    <b-input-group prepend="Rp.">
                        <b-form-input
                            v-model="row.item.to"
                            @input="formatCurrencyInput(row.item, 'to')"
                        />
                    </b-input-group>
                </template>
                <template v-slot:cell(percentage)="row">
                    <b-input-group append="%">
                        <b-form-input
                            v-model="row.item.percentage"
                            @input="formatPercentageInput(row.item)"
                        />
                    </b-input-group>
                </template>
                <template v-slot:cell(action)="row">
                    <div class="d-flex">
                        <b-button
                            size="sm"
                            variant="primary"
                            class="mr-2"
                            v-b-tooltip.hover
                            title="Tambah"
                            @click="addRow(row.index)"
                        >
                            <i class="fa fa-plus" />
                        </b-button>
                        <b-button
                            size="sm"
                            variant="danger"
                            v-b-tooltip.hover
                            title="Hapus"
                            @click="deleteRow(row.index)"
                        >
                            <i class="fa fa-times" />
                        </b-button>
                    </div>
                </template>
            </b-table>

            <b-form-group label="Pembulatan">
                <b-input-group prepend="Rp.">
                    <b-input
                        placeholder="Masukan Pembulatan"
                        @input="formatCurrencyInput(input, 'rounding')"
                        v-model="input.rounding"
                    />
                </b-input-group>
            </b-form-group>
            <div class="mt-4 text-right">
                <b-button
                    variant="primary"
                    :disabled="isSubmitting"
                    @click="confirmCreate"
                >
                    {{ isSubmitting ? "Menyimpan..." : "Simpan" }}
                </b-button>
            </div>
        </b-modal>

        <b-modal
            id="m-edit"
            title="Edit Rumus Harga"
            size="lg"
            no-close-on-backdrop
            no-close-on-esc
            hide-footer
            @hide="clearEdit"
        >
            Mengubah rumus harga
            <strong>{{ activeItem.item_kind_name }}</strong>

            <div class="text-right my-4">
                <b-button variant="dark" @click="addRow(-1, true)">
                    <i class="fa fa-plus mr-2" />Tambah Baris Paling Atas
                </b-button>
            </div>

            <b-table
                bordered
                hover
                show-empty
                responsive
                :items="edit.ranges"
                :fields="fieldsRangeInput"
                empty-text="Tidak ada data range."
            >
                <template v-slot:empty>
                    Tidak ada data range.
                </template>
                <template v-slot:cell(from)="row">
                    <b-input-group prepend="Rp.">
                        <b-form-input
                            v-model="row.item.from"
                            @input="formatCurrencyInput(row.item, 'from')"
                        />
                    </b-input-group>
                </template>
                <template v-slot:cell(to)="row">
                    <b-input-group prepend="Rp.">
                        <b-form-input
                            v-model="row.item.to"
                            @input="formatCurrencyInput(row.item, 'to')"
                        />
                    </b-input-group>
                </template>
                <template v-slot:cell(percentage)="row">
                    <b-input-group append="%">
                        <b-form-input
                            v-model="row.item.percentage"
                            @input="formatPercentageInput(row.item)"
                        />
                    </b-input-group>
                </template>
                <template v-slot:cell(action)="row">
                    <div class="d-flex">
                        <b-button
                            size="sm"
                            variant="primary"
                            class="mr-2"
                            v-b-tooltip.hover
                            title="Tambah"
                            @click="addRow(row.index, true)"
                        >
                            <i class="fa fa-plus" />
                        </b-button>
                        <b-button
                            size="sm"
                            variant="danger"
                            v-b-tooltip.hover
                            title="Hapus"
                            @click="deleteRow(row.index, true)"
                        >
                            <i class="fa fa-times" />
                        </b-button>
                    </div>
                </template>
            </b-table>

            <b-form-group label="Pembulatan" class="mt-2">
                <b-input-group prepend="Rp.">
                    <b-input
                        placeholder="Masukan Pembulatan"
                        @input="formatCurrencyInput(edit, 'rounding')"
                        v-model="edit.rounding"
                    />
                </b-input-group>
            </b-form-group>
            <div class="mt-4 text-right">
                <b-button
                    variant="primary"
                    :disabled="isSubmitting"
                    @click="confirmEdit"
                >
                    {{ isSubmitting ? "Menyimpan..." : "Simpan" }}
                </b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
export default {
    data() {
        return {
            initStatus: 0,
            isBusy: true,
            isSubmitting: false,

            filter: {
                kind: ""
            },

            input: {
                kindId: null,
                rounding: null,
                ranges: []
            },

            edit: {
                rounding: null,
                ranges: []
            },

            detail: {
                item_kind_name: "",
                rounding: "",
                ranges: []
            },

            optKind: [],

            options: [
                { value: 10, text: "10" },
                { value: 20, text: "20" },
                { value: 30, text: "30" },
                { value: 40, text: "40" },
                { value: 50, text: "50" }
            ],

            fields: [
                {
                    key: "item_kind_name",
                    label: "Jenis Barang",
                    sortable: false
                },
                { key: "rounding", label: "Pembulatan", sortable: true },
                { key: "action", label: "Aksi" }
            ],

            fieldsRangeInput: [
                { key: "from", label: "Dari", sortable: false },
                { key: "to", label: "Sampai", sortable: false },
                { key: "percentage", label: "Persentase", sortable: false },
                { key: "action", label: "Aksi" }
            ],

            fieldsRangeDetail: [
                { key: "from", label: "Dari", sortable: false },
                { key: "to", label: "Sampai", sortable: false },
                { key: "percentage", label: "Persentase", sortable: false }
            ],

            items: [],
            activeItem: {
                id: null
            },
            currentPage: 1,
            perPage: 10,
            totalRows: 0,
            sortBy: null,
            sortDesc: false
        };
    },

    methods: {
        init() {
            this.initStatus = 0;
            let getItems = this.getItems();

            let getMasterKind = this.$axios.get("master/kinds").then(res => {
                this.optKind = [
                    { id: null, name: "Pilih Jenis Barang" },
                    ...res.data.data
                ];
            });

            Promise.all([getItems, getMasterKind])
                .then(() => {
                    this.initStatus = 1;
                })
                .catch(() => {
                    this.initStatus = -1;
                });
        },

        clearInput() {
            this.input = {
                kindId: null,
                rounding: null,
                ranges: []
            };
        },

        clearEdit() {
            this.edit = {
                rounding: null,
                ranges: []
            };
        },

        addRow(index, isEdit = false) {
            let ranges = isEdit ? this.edit.ranges : this.input.ranges;
            for (let i = 0; i < ranges.length; i++) {
                let r = ranges[i];
                if (this.$helper.isBlank(r.from)) {
                    return this.$helper.toastError(
                        this,
                        `Range ${i + 1}: Harga minimum tidak boleh kosong!`
                    );
                }
                if (this.$helper.isBlank(r.to)) {
                    return this.$helper.toastError(
                        this,
                        `Range ${i + 1}: Harga maksimum tidak boleh kosong!`
                    );
                }
                if (this.$helper.isBlank(r.percentage)) {
                    return this.$helper.toastError(
                        this,
                        `Range ${i + 1}: Persentase tidak boleh kosong!`
                    );
                }
            }
            ranges.splice(index + 1, 0, { from: "", to: "", percentage: "" });
        },

        deleteRow(index, isEdit = false) {
            let ranges = isEdit ? this.edit.ranges : this.input.ranges;
            ranges.splice(index, 1);
        },

        prepareCreate() {
            this.clearInput();
            this.input.ranges.push({ from: "", to: "", percentage: "" });
            this.$bvModal.show("m-create");
        },

        prepareEdit(item) {
            this.activeItem = item;
            this.edit.rounding = this.$helper.formatThousand(item.rounding, "");
            this.edit.ranges = item.ranges.map(r => ({
                from: this.$helper.formatThousand(r.from, ""),
                to: this.$helper.formatThousand(r.to, ""),
                percentage: String(r.percentage)
            }));
            this.$bvModal.show("m-edit");
        },

        showDetail(item) {
            this.activeItem = item;
            this.detail.item_kind_name = item.item_kind_name;
            this.detail.rounding = this.$helper.formatThousand(item.rounding);
            this.detail.ranges = item.ranges.map(r => ({
                from: this.$helper.formatThousand(r.from),
                to: this.$helper.formatThousand(r.to),
                percentage: r.percentage
            }));
            this.$bvModal.show("m-detail");
        },

        validateRanges(ranges) {
            for (let i = 0; i < ranges.length; i++) {
                let r = ranges[i];
                if (this.$helper.isBlank(r.from)) {
                    return `Range ${i + 1}: Harga dari tidak boleh kosong!`;
                }
                if (this.$helper.isBlank(r.to)) {
                    return `Range ${i + 1}: Harga sampai tidak boleh kosong!`;
                }
                if (this.$helper.isBlank(r.percentage)) {
                    return `Range ${i + 1}: Persentase tidak boleh kosong!`;
                }
            }

            for (let i = 0; i < ranges.length; i++) {
                let fromNum = this.$helper.toNumber(ranges[i].from, null, true);
                let toNum = this.$helper.toNumber(ranges[i].to, null, true);
                if (fromNum >= toNum) {
                    return `Range ${i + 1}: Harga sampai harus lebih besar dari harga dari!`;
                }
            }

            return null;
        },

        confirmCreate() {
            if (this.$helper.isBlank(this.input.kindId)) {
                return this.$helper.toastError(
                    this,
                    "Jenis barang tidak boleh kosong!"
                );
            }

            if (this.$helper.isBlank(this.input.rounding)) {
                return this.$helper.toastError(
                    this,
                    "Pembulatan tidak boleh kosong!"
                );
            }

            if (this.input.ranges.length < 1) {
                return this.$helper.toastError(
                    this,
                    "Range harga minimal harus ada 1!"
                );
            }

            let rangeError = this.validateRanges(this.input.ranges);
            if (rangeError) {
                return this.$helper.toastError(this, rangeError);
            }

            let isConfirmed = confirm("Simpan rumus harga baru ini?");
            if (!isConfirmed) return;

            let payload = {
                item_kind_id: this.input.kindId,
                rounding: this.$helper.toNumber(this.input.rounding, null, true),
                ranges: this.input.ranges.map(r => ({
                    from: this.$helper.toNumber(r.from, null, true),
                    to: this.$helper.toNumber(r.to, null, true),
                    percentage: Number(r.percentage)
                }))
            };

            this.isSubmitting = true;
            this.$axios
                .post("master/marketplace-price-modifiers", payload)
                .then(() => {
                    this.$helper.toastSuccess(
                        this,
                        "Rumus harga berhasil ditambahkan!"
                    );
                    this.$bvModal.hide("m-create");
                    this.getItems(this.currentPage);
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => (this.isSubmitting = false));
        },

        confirmEdit() {
            if (this.$helper.isBlank(this.edit.rounding)) {
                return this.$helper.toastError(
                    this,
                    "Pembulatan tidak boleh kosong!"
                );
            }

            if (this.edit.ranges.length < 1) {
                return this.$helper.toastError(
                    this,
                    "Range harga minimal harus ada 1!"
                );
            }

            let rangeError = this.validateRanges(this.edit.ranges);
            if (rangeError) {
                return this.$helper.toastError(this, rangeError);
            }

            let isConfirmed = confirm("Simpan perubahan rumus ini?");
            if (!isConfirmed) return;

            let payload = {
                rounding: this.$helper.toNumber(this.edit.rounding, null, true),
                ranges: this.edit.ranges.map(r => ({
                    from: this.$helper.toNumber(r.from, null, true),
                    to: this.$helper.toNumber(r.to, null, true),
                    percentage: Number(r.percentage)
                }))
            };

            this.isSubmitting = true;
            this.$axios
                .put("master/marketplace-price-modifiers/" + this.activeItem.id, payload)
                .then(() => {
                    this.$helper.toastSuccess(
                        this,
                        "Rumus harga berhasil diubah!"
                    );
                    this.$bvModal.hide("m-edit");
                    this.getItems(this.currentPage);
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => (this.isSubmitting = false));
        },

        getItems(page = 1) {
            this.isBusy = true;
            this.items = [];

            if (isNaN(page)) {
                this.currentPage = 1;
            } else {
                this.currentPage = page;
            }

            let param = this.$helper.getBasePagedQuery(
                this.currentPage,
                this.perPage
            );

            if (!this.$helper.isBlank(this.filter.kind)) {
                param += "&filterKind=" + this.filter.kind;
            }

            if (!this.$helper.isBlank(this.sortBy)) {
                param += "&sortBy=" + this.sortBy;
                param += "&sortDesc=" + this.sortDesc;
            }

            return this.$axios
                .get("master/marketplace-price-modifiers" + param)
                .then(res => {
                    this.items = res.data.data.items;
                    this.totalRows = res.data.data.total;
                })
                .catch(error => {
                    this.$helper.parseError(this, error);
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },

        formatCurrencyInput(item, field) {
            let cleaned = String(item[field]).replace(/\./g, "");
            let res = cleaned
                .replace(/(?!^-)[^0-9]/g, "")
                .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
            item[field] = res;
        },

        formatPercentageInput(item) {
            item.percentage = String(item.percentage).replace(/[^0-9.]/g, "");
        },

        onLimitChange(newPerPage) {
            this.perPage = newPerPage;
            this.getItems(this.currentPage);
        },

        onPageChange(newPage) {
            this.currentPage = newPage;
            this.getItems(this.currentPage);
        },

        onSortChanged(ctx) {
            this.sortBy = ctx.sortBy;
            this.sortDesc = ctx.sortDesc;
            this.getItems();
        }
    },

    created() {
        this.init();
    }
};
</script>
